import React from "react";
import { Admin, Resource } from "react-admin";
import dataProvider from "./dataProvider";
import authProvider from "./authProvider";
import MyLayout from "components/layout/Layout";
import LoginPage from "pages/auth/LoginPageCustom";
import { createMuiTheme } from "@material-ui/core/styles";
import navModeReducer from "redux/navmode.reducer";
import navModeSaga from "redux/navmode.saga";

//pages
import users from "pages/users";
import schools from "pages/schools";
import levelGroups from "pages/levelGroups";
import levels from "pages/levels";
import areas from "pages/areas";
import busSchedules from "pages/busSchedules";
import students from "pages/students";
import parents from "pages/parents";
import rateCharts from "pages/ratecharts";
import academicYears from "pages/academicYears";
import academicTerms from "pages/academicTerms";
import holidays from "pages/holidays";
import studentAddresses from "pages/studentAddresses";
import studentSchedules from "pages/studentSchedules";
import subcontractors from "pages/subcontractors";
import vehicles from "pages/vehicles";
import drivers from "pages/drivers";
import busAssistants from "pages/busAssistants";
import notTakingStudents from "pages/notTakingStudents";
import routeplans from "pages/routeplans";
import commercialDocuments from "pages/commercialDocuments";
import assignments from "pages/assignments";
import changeRequestForms from "pages/changeRequestForms";
import unapprovedInvoices from "pages/unapprovedInvoices";
import notTakingReports from "pages/notTakingReports";
import notTakings from "pages/notTakings";
import studentPermissions from "pages/studentPermissions";
import companies from "pages/companies";
import personInCharges from "pages/personInCharges";
import customerReceipts from "pages/customerReceipts";
import emailLogs from "./pages/emailLogs";
import unapprovedCreditNotes from "./pages/unapprovedCreditNotes";
import notifications from "./pages/notifications";
import subcontractorAreas from "pages/subcontractorAreas";
import creditNotes from "./pages/creditNotes";
import invoices from "pages/invoices";
import dueRenewalStudents from "./pages/dueRenewalStudents";
import customerCards from "./pages/customerCards";
import subcontractorAdhocFees from "pages/subcontractorAdhocFees";
import subcontractorRateCharts from "pages/subcontractorRateCharts";
import subcontractorPurchaseInvoices from "pages/subcontractorPurchaseInvoices";
import subcontractorTimesheets from "pages/subcontractorTimesheets";
import subcontractorPurchaseInvoiceSummaries from "pages/subcontractorPurchaseInvoiceSummaries";
import subcontractorRouteTransactions from "./pages/subcontractorRouteTransactions";
import ccaAreas from "pages/ccaAreas";
import ccaFixedLocationDropOff from "pages/ccaFixedLocationDropOff";

import customRoutes from "./routes";

import { theme } from "./styles/theme";
import ccaAcademicYears from "pages/ccaAcademicYears";
import ccaAcademicTerms from "pages/ccaAcademicTerms";
import ccaHolidays from "pages/ccaHolidays";
import ccaRateCharts from "pages/ccaRateCharts";
import ccaStudentSchedules from "pages/ccaStudentSchedules";
import ccaDueRenewalStudents from "pages/ccaDueRenewalStudents";
import enhancedRoutePlans from "./pages/enhancedRoutePlans";
import approveCcaStudentRenewalWithSchedules from "./pages/approveCcaStudentRenewalWithSchedules";
import dryRunSchedules from "./pages/dryRunSchedules";
import subcontractorHolidayPayments from "./pages/subcontractorHolidayPayments";
import subcontractorErps from "./pages/subcontractorErps";
import subcontractorAdjustmentPrices from "./pages/subcontractorAdjustmentPrices";
import excludeBusAssistants from "./pages/excludeBusAssistants";
import adhocOperators from "./pages/adhocOperators";
import studentSubsidies from "./pages/studentSubsidies";

const customTheme = createMuiTheme(theme);

const App = () => {
  if (process.env.REACT_APP_STATUS === "STAGING") {
    console.log = function () {};
  }

  return (
    <Admin
      customReducers={{ navMode: navModeReducer }}
      customSagas={navModeSaga}
      dataProvider={dataProvider}
      customRoutes={customRoutes}
      layout={MyLayout}
      theme={customTheme}
      authProvider={authProvider}
      loginPage={LoginPage}
    >
      <Resource name="dashboard" />
      <Resource {...schools} />
      <Resource {...users} />
      <Resource {...levelGroups} />
      <Resource {...levels} />
      <Resource {...academicTerms} />
      <Resource {...academicYears} />
      <Resource {...holidays} />
      <Resource {...rateCharts} />
      <Resource {...areas} />
      <Resource {...busSchedules} />
      <Resource {...students} />
      <Resource {...studentSchedules} />
      <Resource name="studentScheduleDetails" />
      <Resource {...parents} />
      <Resource {...commercialDocuments} />
      <Resource name="billingAddress" />
      <Resource {...studentAddresses} />
      <Resource name="transactions" />
      <Resource {...subcontractors} />
      <Resource {...vehicles} />
      <Resource {...drivers} />
      <Resource {...busAssistants} />
      <Resource {...notTakingStudents} />
      <Resource {...routeplans} />
      <Resource name="customerLedgers" />
      <Resource {...assignments} />
      <Resource name="routes" />
      <Resource name="roles" />
      <Resource {...changeRequestForms} />
      <Resource {...unapprovedInvoices} />
      <Resource {...unapprovedCreditNotes} />
      <Resource {...notTakingReports} />
      <Resource {...notTakings} />
      <Resource name="payments" />
      <Resource {...studentPermissions} />
      <Resource {...companies} />
      <Resource {...personInCharges} />
      <Resource {...customerReceipts} />
      <Resource {...emailLogs} />
      <Resource {...notifications} />
      <Resource {...subcontractorAreas} />
      <Resource {...creditNotes} />
      <Resource {...invoices} />
      <Resource {...dueRenewalStudents} />
      <Resource {...customerCards} />
      <Resource name="customers" />
      <Resource name="admins" />
      <Resource {...subcontractorAdhocFees} />
      <Resource {...subcontractorRateCharts} />
      <Resource {...subcontractorPurchaseInvoices} />
      <Resource {...subcontractorTimesheets} />
      <Resource {...subcontractorPurchaseInvoiceSummaries} />
      <Resource {...subcontractorRouteTransactions} />
      <Resource name="listParentsForNotification" />
      <Resource name="listDriversForNotification" />
      <Resource {...ccaAreas} />
      <Resource {...ccaFixedLocationDropOff} />
      <Resource {...ccaAcademicYears} />
      <Resource {...ccaAcademicTerms} />
      <Resource {...ccaHolidays} />
      <Resource {...ccaRateCharts} />
      <Resource {...ccaStudentSchedules} />
      <Resource {...ccaDueRenewalStudents} />
      <Resource {...approveCcaStudentRenewalWithSchedules} />
      <Resource {...enhancedRoutePlans} />
      <Resource {...dryRunSchedules} />
      <Resource {...subcontractorHolidayPayments} />
      <Resource {...subcontractorErps} />
      <Resource {...subcontractorAdjustmentPrices} />
      <Resource name="subcontractorAdjustmentPriceStatusLogs" />
      <Resource {...excludeBusAssistants} />
      <Resource name="getLatestSubcontractorRoutes" />
      <Resource {...adhocOperators} />
      <Resource {...studentSubsidies} />
    </Admin>
  );
};

export default App;
